import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SettingsState {
    theme: 'classic' | 'hive' | 'light';

    notifications: {
        email: boolean;
        slack: boolean;
        sms: boolean;
        desktop: boolean;
        taskComplete: boolean;
        requiresInput: boolean;
    };

    billingConfig: {
        provider: string;
        apiKey: string;
    };

    workspacePrefs: {
        sidebarCollapsed: boolean;
        fontSize: number;
        autoSave: boolean;
    };

    security: {
        mfaEnabled: boolean;
        allowedOrigins: string[];
        sessionTimeout: number;
    };

    /**
     * Studio v3.6: Manual Mode
     * When false (Auto mode): Icons emit suggestions, Soma mediates all canvas changes
     * When true (Manual mode): Icons act directly, power-user controls visible
     * Default: false (Auto mode)
     */
    manualMode: boolean;

    setTheme: (theme: SettingsState['theme']) => void;
    setNotifications: (notifications: Partial<SettingsState['notifications']>) => void;
    setBillingConfig: (config: Partial<SettingsState['billingConfig']>) => void;
    setWorkspacePrefs: (prefs: Partial<SettingsState['workspacePrefs']>) => void;
    setSecurity: (security: Partial<SettingsState['security']>) => void;
    setManualMode: (enabled: boolean) => void;
}

const defaultState: Omit<
    SettingsState,
    | 'setTheme'
    | 'setNotifications'
    | 'setBillingConfig'
    | 'setWorkspacePrefs'
    | 'setSecurity'
    | 'setManualMode'
> = {
    theme: 'classic',
    notifications: {
        email: true,
        slack: false,
        sms: false,
        desktop: true,
        taskComplete: true,
        requiresInput: true,
    },
    billingConfig: { provider: 'enterprise', apiKey: '' },
    workspacePrefs: { sidebarCollapsed: false, fontSize: 14, autoSave: true },
    security: { mfaEnabled: false, allowedOrigins: [], sessionTimeout: 30 },
    manualMode: false,
};

export const useSettingsStore = create<SettingsState>()(
    persist(
        (set, _get) => ({
            ...defaultState,

            setTheme: theme => {
                set({ theme });
                if (typeof window !== 'undefined') {
                    window.sessionStorage.setItem('beehive-theme', theme);
                    window.dispatchEvent(new CustomEvent('theme-change', { detail: theme }));
                }
            },

            setNotifications: notifications =>
                set(state => ({
                    notifications: { ...state.notifications, ...notifications },
                })),

            setBillingConfig: billingConfig =>
                set(state => ({
                    billingConfig: { ...state.billingConfig, ...billingConfig },
                })),

            setWorkspacePrefs: workspacePrefs =>
                set(state => ({
                    workspacePrefs: { ...state.workspacePrefs, ...workspacePrefs },
                })),

            setSecurity: security =>
                set(state => ({
                    security: { ...state.security, ...security },
                })),

            setManualMode: enabled => set({ manualMode: enabled }),
        }),
        {
            name: 'beehive-settings',
            partialize: state => ({
                theme: state.theme,
                notifications: state.notifications,
                billingConfig: state.billingConfig,
                workspacePrefs: state.workspacePrefs,
                security: state.security,
                manualMode: state.manualMode,
            }),
            merge: (persistedState, currentState) => {
                const persisted = persistedState as Partial<SettingsState> | null;
                return {
                    ...currentState,
                    theme: persisted?.theme || defaultState.theme,
                    notifications: {
                        email:
                            typeof persisted?.notifications?.email === 'boolean'
                                ? persisted.notifications.email
                                : defaultState.notifications.email,
                        slack:
                            typeof persisted?.notifications?.slack === 'boolean'
                                ? persisted.notifications.slack
                                : defaultState.notifications.slack,
                        desktop:
                            typeof persisted?.notifications?.desktop === 'boolean'
                                ? persisted.notifications.desktop
                                : defaultState.notifications.desktop,
                        taskComplete:
                            typeof persisted?.notifications?.taskComplete === 'boolean'
                                ? persisted.notifications.taskComplete
                                : defaultState.notifications.taskComplete,
                        requiresInput:
                            typeof persisted?.notifications?.requiresInput === 'boolean'
                                ? persisted.notifications.requiresInput
                                : defaultState.notifications.requiresInput,
                        sms:
                            typeof persisted?.notifications?.sms === 'boolean'
                                ? persisted.notifications.sms
                                : defaultState.notifications.sms,
                    },
                    billingConfig: {
                        provider:
                            persisted?.billingConfig?.provider ||
                            defaultState.billingConfig.provider,
                        apiKey:
                            persisted?.billingConfig?.apiKey || defaultState.billingConfig.apiKey,
                    },
                    workspacePrefs: {
                        sidebarCollapsed:
                            typeof persisted?.workspacePrefs?.sidebarCollapsed === 'boolean'
                                ? persisted.workspacePrefs.sidebarCollapsed
                                : defaultState.workspacePrefs.sidebarCollapsed,
                        fontSize:
                            typeof persisted?.workspacePrefs?.fontSize === 'number'
                                ? persisted.workspacePrefs.fontSize
                                : defaultState.workspacePrefs.fontSize,
                        autoSave:
                            typeof persisted?.workspacePrefs?.autoSave === 'boolean'
                                ? persisted.workspacePrefs.autoSave
                                : defaultState.workspacePrefs.autoSave,
                    },
                    security: {
                        mfaEnabled:
                            typeof persisted?.security?.mfaEnabled === 'boolean'
                                ? persisted.security.mfaEnabled
                                : defaultState.security.mfaEnabled,
                        allowedOrigins: Array.isArray(persisted?.security?.allowedOrigins)
                            ? persisted.security.allowedOrigins
                            : defaultState.security.allowedOrigins,
                        sessionTimeout:
                            typeof persisted?.security?.sessionTimeout === 'number'
                                ? persisted.security.sessionTimeout
                                : defaultState.security.sessionTimeout,
                    },
                    manualMode:
                        typeof persisted?.manualMode === 'boolean'
                            ? persisted.manualMode
                            : defaultState.manualMode,
                };
            },
        }
    )
);

export const useThemeSettings = () => {
    const theme = useSettingsStore(state => state.theme);
    const setTheme = useSettingsStore(state => state.setTheme);
    return { theme, setTheme };
};

export const useNotificationSettings = () => {
    const notifications = useSettingsStore(state => state.notifications);
    const setNotifications = useSettingsStore(state => state.setNotifications);
    return { notifications, setNotifications };
};

export const useBillingSettings = () => {
    const billingConfig = useSettingsStore(state => state.billingConfig);
    const setBillingConfig = useSettingsStore(state => state.setBillingConfig);
    return { billingConfig, setBillingConfig };
};

export const useWorkspaceSettings = () => {
    const workspacePrefs = useSettingsStore(state => state.workspacePrefs);
    const setWorkspacePrefs = useSettingsStore(state => state.setWorkspacePrefs);
    return { workspacePrefs, setWorkspacePrefs };
};

export const useSecuritySettings = () => {
    const security = useSettingsStore(state => state.security);
    const setSecurity = useSettingsStore(state => state.setSecurity);
    return { security, setSecurity };
};

/**
 * Studio v3.6: Manual Mode hook
 *
 * Auto mode (default): Icons emit suggestions, Soma mediates all canvas changes
 * Manual mode: Icons act directly, power-user controls visible
 */
export const useManualMode = () => {
    const manualMode = useSettingsStore(state => state.manualMode);
    const setManualMode = useSettingsStore(state => state.setManualMode);
    return { manualMode, setManualMode };
};
